import { FunctionComponent, useEffect, useState } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import {
  fetchGet,
  fetchPost,
  fetchPut,
} from "../../../service/api_client";
import { urls } from "../../../utils/urls";
import {
  BusinessUnit,
  BusinessUnitStatus,
  businessUnitDefaultValue,
  ALLOWED_RECURRENCE_MONTHS,
  RECURRENCE_LABELS,
} from "../models/business_unit_model";
import { useAuth } from "../../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../../utils/enums";

const BusinessUnitCreateUpdateScreen: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigation = useHistory();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/org/business-unit`;
  const is_update_page = useRouteMatch(urls.business_unit + "/update/:id");
  const { showToast } = useToast();
  const { isAuthorised } = useAuth();
  // Phase 4: annual_estimate is EAF-only (matches backend gating).
  const canEditAnnual = isAuthorised({
    module: MODULE.eaf,
    action: ACTION.UPDATE,
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<BusinessUnit>({ ...businessUnitDefaultValue });

  useEffect(() => {
    if (id && is_update_page) {
      getData(id);
    }
  }, [id]);

  const getData = async (id: string) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + "/" + id);
      if (res.success) {
        setData(res.data);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    }
    setLoading(false);
  };

  const onSubmit = async () => {
    if (!data.name || !data.code) {
      showToast({
        type: ToastType.error,
        text: "Please fill name and code.",
      });
      return;
    }
    setLoading(true);
    try {
      const res = data.id
        ? await fetchPut(`${url}/${data.id}`, data)
        : await fetchPost(url, data);
      if (res.success) {
        showToast({ type: ToastType.success, text: res.message });
        navigation.push(urls.business_unit);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    }
    setLoading(false);
  };

  return (
    <>
      <div className="flex justify-between items-end my-1">
        <h1 className="flex text-xl font-bold border-l-4 border-myPrimaryColor mx-5 pl-2">
          Business Unit {id ? `Update: ${data.id}` : "Create"}
        </h1>
      </div>

      <div className="m-5 p-5 shadow bg-white rounded-lg flex flex-col gap-5">
        <div className="flex flex-col items-start text-sm w-full md:w-1/2">
          <label className="font-semibold w-full" htmlFor="name">
            Name*
          </label>
          <input
            placeholder="e.g. Operations"
            className="p-1 rounded focus:outline-none w-full border"
            id="name"
            name="name"
            value={data.name || ""}
            type="text"
            onChange={(e) => setData({ ...data, name: e.target.value })}
          />
        </div>

        <div className="flex flex-col items-start text-sm w-full md:w-1/2">
          <label className="font-semibold w-full" htmlFor="code">
            Code*
          </label>
          <input
            placeholder="e.g. OPS"
            className="p-1 rounded focus:outline-none w-full border"
            id="code"
            name="code"
            value={data.code || ""}
            type="text"
            onChange={(e) =>
              setData({ ...data, code: e.target.value.toUpperCase() })
            }
          />
        </div>

        <div className="flex flex-col items-start text-sm w-full md:w-1/4">
          <label className="font-semibold w-full" htmlFor="status">
            Status
          </label>
          <select
            id="status"
            className="p-1 rounded focus:outline-none w-full border"
            value={data.status || BusinessUnitStatus.active}
            onChange={(e) =>
              setData({
                ...data,
                status: e.target.value as typeof data.status,
              })
            }
          >
            <option value={BusinessUnitStatus.active}>Active</option>
            <option value={BusinessUnitStatus.inactive}>Inactive</option>
          </select>
        </div>

        {/* Phase 4: annual estimate (EAF-only). Server seeds monthly_slices
            from this on first set / FY rollover / annual change. */}
        {canEditAnnual && (
          <div className="flex flex-col md:flex-row gap-5 w-full">
            <div className="flex flex-col items-start text-sm w-full md:w-1/2">
              <label className="font-semibold w-full" htmlFor="annual_estimate">
                Annual Estimate (₹)
              </label>
              <input
                id="annual_estimate"
                type="number"
                min={0}
                placeholder="e.g. 12000000"
                className="p-1 rounded focus:outline-none w-full border"
                value={
                  data.annual_estimate === undefined ? "" : data.annual_estimate
                }
                onChange={(e) =>
                  setData({
                    ...data,
                    annual_estimate:
                      e.target.value === "" ? undefined : Number(e.target.value),
                  })
                }
              />
              <span className="text-xs text-gray-500 mt-1">
                Re-seeds monthly slices when changed. Recalibrated by month-end close.
              </span>
            </div>
            <div className="flex flex-col items-start text-sm w-full md:w-1/2">
              <label className="font-semibold w-full" htmlFor="current_fy">
                Financial Year
              </label>
              <input
                id="current_fy"
                type="text"
                placeholder="e.g. 2026-2027"
                className="p-1 rounded focus:outline-none w-full border"
                value={data.current_fy || ""}
                onChange={(e) =>
                  setData({ ...data, current_fy: e.target.value })
                }
              />
              <span className="text-xs text-gray-500 mt-1">
                Leave blank to default to current FY.
              </span>
            </div>
            {/* Phase 7C: planning cadence */}
            <div className="flex flex-col items-start text-sm w-full md:w-1/2">
              <label
                className="font-semibold w-full"
                htmlFor="recurrence_months"
              >
                Recalibration Cadence
              </label>
              <select
                id="recurrence_months"
                className="p-1 rounded focus:outline-none w-full border"
                value={data.recurrence_months ?? 1}
                onChange={(e) =>
                  setData({
                    ...data,
                    recurrence_months: Number(e.target.value),
                  })
                }
              >
                {ALLOWED_RECURRENCE_MONTHS.map((n) => (
                  <option key={n} value={n}>
                    {RECURRENCE_LABELS[n]} (every {n} mo)
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500 mt-1">
                How often the monthly-close engine recalibrates this BU.
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 mx-5 mb-10">
        <button
          onClick={() => navigation.push(urls.business_unit)}
          type="button"
          className="rounded-md border border-gray-400 text-sm font-semibold bg-white text-black px-4 py-1.5"
        >
          Cancel
        </button>
        <button
          onClick={onSubmit}
          type="button"
          className="bg-myPrimaryColor text-white rounded-md px-6 py-1.5 text-sm"
        >
          {id ? "Update" : "Create"}
        </button>
      </div>

      {loading && <LoadingWidget />}
    </>
  );
};

export default BusinessUnitCreateUpdateScreen;
